import type { FunctionComponent } from 'react'
import React, { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import type { MapStackTraceOptions as LibraryOptions } from 'sourcemapped-stacktrace'

import { CLIENT_ERROR_CODE } from '@app/constants/Misc'

import { getClipboardManager } from '@app/services/ClipboardManager'

import { captureException } from '@app/utils/errorReport/errorReport'
import { reportSpawnFailure } from '@app/utils/errorReport/reportSpawnFailure'
import moment from '@app/utils/moment'
import { noop } from '@app/utils/noop'
import { useAppDispatch } from '@app/utils/redux'
import { maybeType, valita } from '@app/utils/valita'

import { analyticsEvent, noEvent } from '@app/packages/analytics/declaration'
import { isApiError } from '@app/packages/api/errors'
import { spawn } from '@app/packages/disposable/spawn'
import { isApiError as isInternalApiError } from '@app/packages/internalApi/errors'

import { useDisposableEffect } from '@app/hooks/useDisposableEffect'
import { useEvent } from '@app/hooks/useEvent'

import { Button } from '@app/components/Button/Button'
import { ButtonTypeInline } from '@app/components/Button/ButtonType'
import { CryingChild } from '@app/components/CryingChild/CryingChild'
import type { FallbackRender } from '@app/components/ErrorBoundary'
import { ErrorBoundary } from '@app/components/ErrorBoundary'
import { Icon } from '@app/components/Icon/Icon'
import { Logo } from '@app/components/Logo/Logo'

import Typography from '@app/components/Typography/Typography.module.scss'
import classes from './ErrorPage.module.scss'

export interface Props {
  error?: any
  errorID?: string
  info?: string
  ips?: string[]
}

export const ErrorPage: FunctionComponent<Props> = ({ error, errorID, info, ips }) => {
  const { formatMessage } = useIntl()

  const maybeError = maybeType(RT_Error, error)
  const code = maybeError?.status ?? CLIENT_ERROR_CODE
  const message = maybeError?.message ?? 'Unknown error'
  const localizedMessage = maybeError?.localizedMessage ?? (code === 404 ? formatMessage(codes[404]) : formatMessage(messages.code_default))
  const handled = !!maybeError && 'handled' in maybeError && !!maybeError.handled

  const shouldshowSettingsInsteadOfLogo = message === 'Email confirmation fetch failed'

  return (
    <div className={classes.root}>
      <div className={classes.layout}>
        <Logo />
        <div className={classes.content}>
          <CryingChild />
          <h1 className={classes.code}>{code}</h1>
          <h2 className={cn(Typography.landing_main_text, classes.title)}>{localizedMessage}</h2>
          <Button
            action={plainLinkAction}
            className={classes.home}
            render={({ className }) => (
              <a className={className} href={shouldshowSettingsInsteadOfLogo ? '/settings' : '/'}>
                {formatMessage(shouldshowSettingsInsteadOfLogo ? messages.go_to_settings : messages.go_to_main)}
              </a>
            )}
          />
          {info && !handled ? (
            <ErrorBoundary fallback={SkippedDebugDetails}>
              <DebugDetails error={error} errorID={errorID} info={info} ips={ips} />
            </ErrorBoundary>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** Read off the stack itself: the app's WebView names no browser the library knows, and it throws on that (FRONTEND-95B). */
export const traceFormatOf = (stack: string): MapStackTraceOptions['traceFormat'] => (/^\s+at /m.test(stack) ? 'chrome' : 'firefox')

const DebugDetails: FunctionComponent<Omit<Props, 'info'> & { info: string }> = ({ error, errorID, info, ips }) => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const copyTimeout = useRef<undefined | number>(undefined)
  const [copied, setCopied] = useState(false)
  const [stack, setStack] = useState('')
  const now = useMemo(() => moment(), [])

  const debugInfo = useMemo(() => {
    const o: string[] = []

    if (errorID) {
      o.push(formatMessage(messages.error_id, { id: errorID }))
    }

    o.push(formatMessage(messages.timestamp, { time: now.format() }))
    if (ips?.length) {
      o.push(formatMessage(messages.ip, { ip: ips.at(0) ?? 'unknown' }))
    }
    if (IS_BROWSER) o.push(formatMessage(messages.user_agent, { agent: navigator.userAgent }))

    o.push([getErrorMessage(error), ...getCauses(error).map(c => 'Cause: ' + c)].join('\n'))

    if (stack) {
      o.push(stack)
    } else if ('stack' in error) {
      o.push(error.stack)
    }

    o.push(info)

    // Stacks come with blank lines at their ends; the indent of their first frame stays.
    return o.map(part => part.replace(/^\s*\n|\n\s*$/g, '')).join('\n\n')
  }, [info, errorID, formatMessage, now, ips, error, stack])

  const handleCopy = useEvent(() => {
    dispatch(getClipboardManager()).copy(debugInfo)
    setCopied(true)
    window.clearTimeout(copyTimeout.current)
    copyTimeout.current = window.setTimeout(() => {
      setCopied(false)
    }, 1500)
  })

  const mapErrorStack = useEffectEvent((stack: DisposableStack) => {
    if (error instanceof Error) {
      stack.use(
        spawn(async stack => {
          setStack(error.stack ?? '')
          const { mapStackTrace } = await import('sourcemapped-stacktrace')
          if (stack.disposed) return
          const options: MapStackTraceOptions = { traceFormat: traceFormatOf(error.stack ?? '') }
          mapStackTrace(
            error.stack ?? '',
            errStack => {
              if (stack.disposed) return
              setStack(errStack.join('\n'))
            },
            options
          )
        }, reportSpawnFailure('ErrorPage'))
      )
    }
  })

  useDisposableEffect(stack => mapErrorStack(stack), [])

  return (
    <div className={classes.debug}>
      <Button
        action={{
          event: analyticsEvent(() => ({
            // Copied the error details, which is what support asks for.
            type: 'error_page.copy_button.click',
          })),
          handler: handleCopy,
        }}
        aria-label={formatMessage(copied ? messages.copied : messages.copy)}
        className={classes.copy_button}
        htmlType="button"
        type={ButtonTypeInline.getInstance()}
      >
        <Icon className={classes.copy_icon} icon={copied ? 'check' : 'copy'} />
      </Button>
      <div className={classes.debug_text}>{formatMessage(messages.dev_info) + ':\n\n' + debugInfo}</div>
    </div>
  )
}

/** The details are for developers: ones that cannot be drawn are reported, and the page stands without them. */
const SkippedDebugDetails: FallbackRender = ({ error }) => {
  useEffect(() => {
    captureException(error)
  }, [error])

  return null
}

/** The button is only a look: the page renders without a router, and a broken app is left by a full load. */
const plainLinkAction = { event: noEvent, handler: noop }

const messages = defineMessages({
  code_default: { id: "components.ErrorPage.ErrorPage.messages.code_default.f97d", defaultMessage: 'Что-то сломалось' },
  error_id: { id: "components.ErrorPage.ErrorPage.messages.error_id.7e25", defaultMessage: 'Идентификатор ошибки: {id}' },
  timestamp: { id: "components.ErrorPage.ErrorPage.messages.timestamp.d972", defaultMessage: 'Время: {time}' },
  ip: { id: "components.ErrorPage.ErrorPage.messages.ip.1889", defaultMessage: 'IP адрес: {ip}' },
  user_agent: { id: "components.ErrorPage.ErrorPage.messages.user_agent.acec", defaultMessage: 'Юзер-aгент: {agent}' },
  copy: { id: "components.ErrorPage.ErrorPage.messages.copy.bf4f", defaultMessage: 'скопировать' },
  copied: { id: "components.ErrorPage.ErrorPage.messages.copied.9ec0", defaultMessage: 'скопировано' },
  dev_info: { id: "components.ErrorPage.ErrorPage.messages.dev_info.e308", defaultMessage: 'Информация для разработчиков' },

  go_to_main: { id: "components.ErrorPage.ErrorPage.messages.go_to_main.3ddd", defaultMessage: 'На главную' },
  go_to_settings: { id: "components.ErrorPage.ErrorPage.messages.go_to_settings.c919", defaultMessage: 'Настройки' },
})

const codes = defineMessages({
  '404': { id: "components.ErrorPage.ErrorPage.codes.404.bcec", defaultMessage: 'Страница не найдена' },
  '502': { id: "components.ErrorPage.ErrorPage.codes.502.2502", defaultMessage: 'Не удалось загрузить данные' },
})

const RT_Error = valita.object({
  status: valita.number().optional(),
  message: valita.string().optional(),
  localizedMessage: valita.string().optional(),
  cause: valita.unknown().optional(),
})

const getErrorMessage = (error: any): string => {
  // an api failure carries the response it failed with, and that is the part worth reading
  if (isApiError(error) || isInternalApiError(error)) return JSON.stringify(error, undefined, 2)

  try {
    if ('message' in error) {
      return (error.name || 'Error') + ': ' + error.message
    }
  } catch {}

  return String(error)
}

const getCauses = (error: any): string[] => {
  const causes: string[] = []
  let cause = maybeType(RT_Error, error)?.cause
  while (cause) {
    if (typeof cause === 'string') {
      causes.push(cause)
      break
    }
    if (cause instanceof Error) {
      causes.push(cause.message)
      cause = cause.cause
      continue
    }
    const me = maybeType(RT_Error, cause)
    if (me) {
      causes.push(me.message ?? 'Unknown error')
      cause = me.cause
      continue
    }
    break
  }
  return causes
}

/** Missing from the library's types; without it the library guesses from the user agent. */
type MapStackTraceOptions = LibraryOptions & { traceFormat: 'chrome' | 'firefox' }
